/**
 * DialogWindow.js
 * Logic for the displaying of the dialog window.
 *
 * @class   DialogWindow
 * @author  Yevgeniy Rokhlin
 *
 */

import React from 'react/addons';
import WindowListeners from 'mixins/js/WindowListeners';
import CssEvent from 'utils/css-event';
import KeyCode from 'utils/key-code';
import ClassNames from 'classnames';
import FlatButton from 'components/buttons/flat-button/FlatButton';
import RaisedButton from 'components/buttons/raised-button/RaisedButton';
import Divider from 'components/divider/Divider';
import Overlay from './overlay';
import Paper from '../paper/Paper';

export default React.createClass({

	mixins: [ WindowListeners],

	propTypes: {
		actions: React.PropTypes.array,
		contentClassName: React.PropTypes.string,
		openImmediately: React.PropTypes.bool,
		onClickAway: React.PropTypes.func,
		onDismiss: React.PropTypes.func,
		onShow: React.PropTypes.func,
		repositionOnUpdate: React.PropTypes.bool,
		animateFromOrigin: React.PropTypes.bool,
		status: React.PropTypes.string,
		trigger: React.PropTypes.shape({
			type: React.PropTypes.oneOf(['button','link','icon']).isRequired,
			text: React.PropTypes.any.isRequired,
			status: React.PropTypes.string,
			triggerClass: React.PropTypes.string
		}),
	},

	// Attach listeners to monitor for scroll event and 'ESC' key up.
	windowListeners: {
		'keyup': '_handleWindowKeyUp',
		'scroll': '_handleScroll'
	},

	getDefaultProps() {
		return {
			actions: [],
			repositionOnUpdate: true,
			status: "secondary",
			animateFromOrigin: false,
		};
	},

	getInitialState() {
		return {
			open: this.props.openImmediately || false
		};
	},

	componentDidMount() {
		//When component mounts, pre-position the dialog.
		this._positionDialog();

		if (this.props.openImmediately) {
			this.props.animateFromOrigin = false;
			this.refs.dialogOverlay.preventScrolling();
			this._onShow();
		}
	},

	componentDidUpdate(prevProps, prevState) {
		this._positionDialog();
	},

	render() {

		let classes = ClassNames('lui-dialog-window', this.props.className, {
			'lui-is-shown': this.state.open
		});

		let contentClasses = 'lui-dialog-window-contents';
		let actions = this._getActionsContainer(this.props.actions);

		// If a contentClassName was defined in props, add it to the class string
		if (this.props.contentClassName) {
			contentClasses += ' ' + this.props.contentClassName;
		};


		// Defines the trigger, and set up switch statement based on trigger type.
		let trigger;

		if (this.props.trigger) {
			switch(this.props.trigger.type) {
				case 'button':
				case 'RaisedButton':
					trigger = (
						<RaisedButton
							status={this.props.trigger.status}
							onClick={this.show}
							label={this.props.trigger.text}
							ref='dialogButton' />
					);
					break;
				case 'FlatButton':
					trigger = (
						<FlatButton
							status={this.props.trigger.status}
							onClick={this.show}
							label={this.props.trigger.text}
							ref='dialogButton' />
					);
					break;
				case 'link':
					trigger = <a ref='dialogButton' onClick={this.show}>{this.props.trigger.text}</a>;
					break;
				case 'icon':
					trigger = <a ref='dialogButton' className={this.props.trigger.classes} onClick={this._toggleModal}><i className={this.props.trigger.text}></i></a>;
					break;
			};
		}
		return (
			<div>
				{trigger}
				<div ref="container" className={classes}>

					<Paper ref="dialogWindow" className={contentClasses} zDepth={4}>
						{this.props.children}
						<Divider marginBottom={5}/>
						{actions}
					</Paper>

					<Overlay ref="dialogOverlay" show={this.state.open} autoLockScrolling={false} onClick={this._handleOverlayTouchTap} />

				</div>
			</div>
		);
	},

	/**
	 * Determines whether or not the dialog is open
	 *
	 * @return {Boolean}	Returns whether dialog is open or not True/False
	 */
	isOpen() {
		return this.state.open;
	},

	/**
	 * Dismisses the dialog, and allows scrolling again.
	 */
	dismiss() {

		//After the dismiss CSS transition is finished, allow scrolling again.
		CssEvent.onTransitionEnd(this.refs.container.getDOMNode(), function() {
			this.refs.dialogOverlay.allowScrolling();
		}.bind(this));

		this.setState({ open: false });
		this._onDismiss();
	},

	/**
	 * Shows the dialog and prevents scrolling.
	 */
	show() {

		this.refs.dialogOverlay.preventScrolling();

		this.setState({ open: true });
		this._onShow();
	},

	/**
	 * Adds a class name to a reactObject's className prop.
	 *
	 * @param {React Element} reactObject	The react object, that you want to add a class to.
	 * @param {string} 		  className 	Name of the class you are adding.
	 */
	_addClassName(reactObject, className) {

		let originalClassName = reactObject.props.className;

		return React.addons.cloneWithProps(reactObject, {
			className: originalClassName ?
				ClassNames(reactObject.props.className, className) : className,
			key: reactObject.key
		});
	},

	/**
	 * Create action buttons
	 *
	 * @param  {JSON} 	actionJSON 		JSON, for standard actions.
	 * @param  {number} key  			Individual key
	 * @return {FlatButton} 			Returns a button, created from the JSON
	 */
	_getAction(actionJSON, key) {

		// If an action does not have an onClick assigned, dismiss the dialog.
		let onClickHandler = actionJSON.onClick ? actionJSON.onClick : this.dismiss;

		return (
			<FlatButton
				key={key}
				status={this.props.status}
				onClick={onClickHandler}
				label={actionJSON.text} />
		);
	},

	/**
	 * Create the container for the action buttons, if actions are JSON and not
	 * custom Action buttons, call _getAction to create the buttons.
	 *
	 * @param  {JSON || reactElement} 	actions 	Either a React Element for custom actions, or JSON describing standard actions.
	 * @return {reactElement}						Returns the created action container.
	 */
	_getActionsContainer(actions) {

		let actionContainer;
		let actionObjects = [];

		if (actions.length) {
			for (let i = 0; i < actions.length; i++) {

				let currentAction = actions[i];

				// If the current action isn't a react object, create one.
				if (!React.isValidElement(currentAction)) {
					currentAction = this._getAction(currentAction, i);
				};

				let newCurrentAction = this._addClassName(currentAction, 'lui-dialog-window-action');

				actionObjects.push(newCurrentAction);
			};

			actionContainer = (
				<div className="lui-dialog-window-actions">
					{actionObjects}
				</div>
			);
		}

		return actionContainer;
	},

	/**
	 * Positions the dialog either to the center, or the trigger origin.
	 */
	_positionDialog() {

		let container, dialogWindow, containerHeight, dialogWindowHeight;

		if (this.refs.dialogButton) {
			this._positionDialogToOrigin();
		}

		if (this.state.open) {
			container = this.refs.container.getDOMNode(),
			dialogWindow = this.refs.dialogWindow.getDOMNode(),
			containerHeight = container.offsetHeight;

			dialogWindow.style.transform = '';

			//Reset the height in case the window was resized.
			dialogWindow.style.height = '';
			dialogWindowHeight = dialogWindow.offsetHeight;

			//Vertically center the dialog window, but make sure it doesn't
			//transition to that position.
			if (this.props.repositionOnUpdate || !container.style.paddingTop) {
			container.style.paddingTop =
				((containerHeight - dialogWindowHeight) / 2) - 64 + 'px';
			};
		};

	},

	/**
	 * Get the X, Y position of an element, used to figure out the origin position of the trigger.
	 *
	 * @param  {element} element 	Any element that has been rendered in the DOM
	 * @return {object}         	The element's X, Y coordinates in an object
	 */
	_getOriginPosition(element) {

		let xPosition = 0,
			yPosition = 0;

		while(element) {
			xPosition += (element.offsetLeft - element.scrollLeft + element.clientLeft);
			yPosition += (element.offsetTop - element.scrollTop + element.clientTop);
			element = element.offsetParent;
		}
		return { x: xPosition, y: yPosition };

	},

	/**
	 * Adjust a set of given coordinates to work from center of screen, instead of top left corner.
	 *
	 * @param  {number} x 	x position value
	 * @param  {number} y 	y position value
	 * @return {object}   	Returns an object with adjusted coordinates and adds 'px' at the end of the x and y values.
	 */
	_getAdjustedOriginPosition(x, y) {

		let xPosition = 0,
			yPosition = 0,
			container = this.refs.container.getDOMNode(),
			dialogWindow = this.refs.dialogWindow.getDOMNode(),
			containerHeight = container.offsetHeight,
			dialogWindowHeight = dialogWindow.offsetHeight;

			xPosition += x - dialogWindow.offsetLeft;
			yPosition += y - ((containerHeight - dialogWindowHeight) / 2) + 64;

			return { x: xPosition + 'px' , y: yPosition + 'px'};

	},

	/**
	 * Moves the dialog to the position of the origin trigger.
	 */
	_positionDialogToOrigin() {
		let originPosition = this._getOriginPosition(this.refs.dialogButton.getDOMNode()),
			adjustedPosition = this._getAdjustedOriginPosition(originPosition.x, originPosition.y),
			dialogWindow = this.refs.dialogWindow.getDOMNode();

		if (this.props.animateFromOrigin && this.props.trigger) {
			dialogWindow.style.transform = 'translate(' + adjustedPosition.x + ',' + adjustedPosition.y + ')' + ' scale(0.1 , 0.1)';
		};

	},

	_onShow() {

		if (this.props.onShow) this.props.onShow();

	},

	_onDismiss() {

		if (this.props.onDismiss) this.props.onDismiss();

	},

	_handleOverlayTouchTap() {

		this.dismiss();
		if (this.props.onClickAway) this.props.onClickAway();

	},

	_handleWindowKeyUp(e) {

		if (e.keyCode == KeyCode.ESC) {
			this.dismiss();
		};

	},

	_handleScroll() {

		if (this.props.trigger) {
			this._positionDialogToOrigin();
		};
	},


});



/** WEBPACK FOOTER **
 ** ./src/components/dialog/DialogWindow.js
 **/